/*
 * 模块: CalculationTab
 * 职责: 渲染计算步骤标签页（详细计算逻辑和步骤）
 * 依赖: antd组件、类型定义、FormattedText组件
 * 导出: CalculationTab组件
 * 限制: 文件<280行, 函数<30行
 */

import React from 'react';
import { Card, Steps, Alert, Space, Typography } from 'antd';
import { BulbOutlined } from '@ant-design/icons';
import type { MetricDefinition } from '../../../types';
import FormattedText from '../FormattedText';

const { Text } = Typography;

interface CalculationTabProps {
  definition: MetricDefinition;
}

const renderRealCaseSteps = (realCase: any) => {
  return (
    <Steps
      direction="vertical"
      current={realCase.step_by_step.length}
      items={realCase.step_by_step.map((step: any, index: number) => ({
        title: (
          <Text strong style={{ fontSize: '15px' }}>
            步骤 {index + 1}: {step.title}
          </Text>
        ),
        description: (
          <Space direction="vertical" style={{ width: '100%', marginTop: 12 }} size="middle">
            {step.detail && (
              <Card size="small" style={{ background: '#fafafa' }}>
                <FormattedText text={step.detail} />
              </Card>
            )}
            {step.code && (
              <div className="code-block">
                <pre>{step.code}</pre>
              </div>
            )}
            {step.result && (
              <Alert
                message="计算结果"
                description={
                  <Space direction="vertical">
                    {Object.entries(step.result).map(([resKey, value]) => (
                      <Text key={resKey}>
                        <Text code>{resKey}</Text> = {' '}
                        <Text strong style={{ color: '#52c41a' }}>
                          {typeof value === 'number' ? value.toFixed(5) : String(value)}
                        </Text>
                      </Text>
                    ))}
                  </Space>
                }
                type="success"
                showIcon
                style={{ background: '#f6ffed' }}
              />
            )}
          </Space>
        ),
      }))}
    />
  );
};

const renderCalculationSteps = (steps: any[]) => {
  return (
    <Steps
      direction="vertical"
      current={steps.length}
      items={steps.map((step) => ({
        title: (
          <Text strong style={{ fontSize: '15px' }}>
            {step.title}
          </Text>
        ),
        description: (
          <Space direction="vertical" style={{ width: '100%', marginTop: 12 }}>
            <div className="code-block">
              <pre>{step.code}</pre>
            </div>
            {step.example && (
              <Alert
                message={<Text strong>示例</Text>}
                description={step.example}
                type="info"
                showIcon
                icon={<BulbOutlined />}
              />
            )}
            {step.explanation && (
              <Alert
                message={step.explanation}
                type="warning"
                showIcon
              />
            )}
          </Space>
        ),
        status: 'finish',
      }))}
    />
  );
};

export const CalculationTab: React.FC<CalculationTabProps> = ({ definition }) => {
  const hasRealCase = definition.real_case;
  const hasCalculationSteps = definition.calculation_steps && definition.calculation_steps.length > 0;

  if (!hasRealCase && !hasCalculationSteps) {
    return (
      <Alert
        message="暂无详细计算步骤"
        type="info"
        showIcon
      />
    );
  }

  return (
    <Card size="small">
      {hasRealCase && renderRealCaseSteps(definition.real_case)}
      {!hasRealCase && hasCalculationSteps && definition.calculation_steps && renderCalculationSteps(definition.calculation_steps)}
    </Card>
  );
};

